import { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Stack,
	Typography,
} from '@mui/material';

function normalizeStatus(item) {
	const status = String(item?.status || item?.result_status || item?.answer_status || '').trim().toLowerCase();
	const explicitCorrect = item?.is_correct ?? item?.correct ?? item?.was_correct ?? item?.answered_correctly ?? null;
	const explicitIncorrect = item?.is_incorrect ?? item?.incorrect ?? item?.was_incorrect ?? item?.answered_incorrectly ?? null;

	if (status === 'correct' || explicitCorrect === true || status === 'answered_correctly' || status === 'right') {
		return 'correct';
	}

	if (
		status === 'incorrect' ||
		explicitIncorrect === true ||
		explicitCorrect === false ||
		status === 'answered_incorrectly' ||
		status === 'wrong' ||
		status === 'failed'
	) {
		return 'incorrect';
	}

	if (status === 'answered' || status === 'reviewed' || status === 'attempted' || status === 'submitted') {
		return 'answered';
	}

	const hasAnswerSignal =
		(item?.selected_option_id !== undefined && item?.selected_option_id !== null) ||
		(item?.option_id !== undefined && item?.option_id !== null) ||
		(item?.answer_option_id !== undefined && item?.answer_option_id !== null) ||
		(item?.response_option_id !== undefined && item?.response_option_id !== null) ||
		Boolean(item?.answer_text || item?.response_text || item?.user_answer || item?.answered_at || item?.answered_at_timestamp);

	return hasAnswerSignal ? 'answered' : 'unanswered';
}

function getQuestionStyles(status, isActive) {
	const baseByStatus = {
		correct: {
			bg: '#dcfce7',
			border: '#86efac',
			text: '#166534',
		},
		incorrect: {
			bg: '#fecaca',
			border: '#f87171',
			text: '#991b1b',
		},
		answered: {
			bg: '#dbeafe',
			border: '#60a5fa',
			text: '#1d4ed8',
		},
		unanswered: {
			bg: '#f8fafc',
			border: '#d1d5db',
			text: '#64748b',
		},
	};

	const style = baseByStatus[status] || baseByStatus.unanswered;

	if (!isActive) return style;

	return {
		bg: '#eef2ff',
		border: '#6366f1',
		text: '#4f46e5',
	};
}

export default function CardNavPreguntas({
	resultsPayload,
	title = 'Navegador de preguntas',
	itemsPerPage = 40,
	currentQuestionPosition = null,
	onQuestionSelect,
}) {
	const [page, setPage] = useState(0);

	const navigatorItems = useMemo(() => {
		return (resultsPayload?.navigator || [])
			.map((item) => ({
				...item,
				position: Number(item?.position),
				normalizedStatus: normalizeStatus(item),
			}))
			.filter((item) => Number.isFinite(item.position) && item.position > 0)
			.sort((a, b) => a.position - b.position);
	}, [resultsPayload]);

	const totalQuestions = navigatorItems.length;
	const totalPages = Math.max(1, Math.ceil(totalQuestions / itemsPerPage));

	useEffect(() => {
		if (page > totalPages - 1) {
			setPage(Math.max(0, totalPages - 1));
		}
	}, [page, totalPages]);

	useEffect(() => {
		const position = Number(currentQuestionPosition);
		if (!Number.isFinite(position) || position <= 0) return;

		const nextPage = Math.floor((position - 1) / itemsPerPage);
		if (nextPage !== page) {
			setPage(nextPage);
		}
	}, [currentQuestionPosition, itemsPerPage, page]);

	const pageItems = useMemo(() => {
		const start = page * itemsPerPage;
		return navigatorItems.slice(start, start + itemsPerPage);
	}, [navigatorItems, page, itemsPerPage]);

	const statusCounts = useMemo(() => {
		return navigatorItems.reduce(
			(acc, item) => {
				const status = item?.normalizedStatus;
				if (status === "correct") acc.correct += 1;
				else if (status === "incorrect") acc.incorrect += 1;
				else if (status === "unanswered") acc.unanswered += 1;
				return acc;
			},
			{ correct: 0, incorrect: 0, unanswered: 0 }
		);
	}, [navigatorItems]);

	return (
		<div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
			<div className="rounded-t mb-0 px-4 py-3 border-0">
				<div className="flex flex-wrap items-center">
					<div className="relative w-full px-4 max-w-full flex-grow flex-1">
						<h3 className="font-semibold text-base text-blueGray-700">{title}</h3>
					</div>
				</div>
			</div>

			<div className="px-6 pb-6 pt-2">

			<Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
				<Stack direction="row" spacing={0.8} alignItems="center">
					<Box sx={{ width: 9, height: 9, borderRadius: '9999px', backgroundColor: '#22c55e' }} />
					<Typography sx={{ fontSize: 12, color: '#64748b' }}>Acierto ({statusCounts.correct})</Typography>
				</Stack>

				<Stack direction="row" spacing={0.8} alignItems="center">
					<Box sx={{ width: 9, height: 9, borderRadius: '9999px', backgroundColor: '#dc2626' }} />
					<Typography sx={{ fontSize: 12, color: '#64748b'}}>Incorrecta ({statusCounts.incorrect})</Typography>
				</Stack>

				<Stack direction="row" spacing={0.8} alignItems="center">
					<Box sx={{ width: 9, height: 9, borderRadius: '9999px', backgroundColor: '#d1d5db' }} />
					<Typography sx={{ fontSize: 12, color: '#64748b' }}>Sin responder ({statusCounts.unanswered})</Typography>
				</Stack>
			</Stack>

			<Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600, mb: 1.3 }}>
				Sin responder: {statusCounts.unanswered} de {totalQuestions}
			</Typography>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: 'repeat(5, minmax(0, 1fr))',
						sm: 'repeat(8, minmax(0, 1fr))',
						md: 'repeat(10, minmax(0, 1fr))',
					},
					gap: 1,
				}}
			>
				{pageItems.map((item) => {
					const isActive = Number(currentQuestionPosition) === item.position;
					const colorSet = getQuestionStyles(item.normalizedStatus, isActive);

					return (
						<Button
							key={item.question_id || item.position}
							onClick={() => onQuestionSelect?.(item)}
							sx={{
								minWidth: 'unset',
								width: '100%',
								height: 32,
								borderRadius: '8px',
								fontSize: 13,
								fontWeight: 700,
								border: `1px solid ${colorSet.border}`,
								backgroundColor: colorSet.bg,
								color: colorSet.text,
								px: 0,
								'&:hover': {
									backgroundColor: colorSet.bg,
									opacity: 0.9,
								},
							}}
						>
							{item.position}
						</Button>
					);
				})}
			</Box>

			{totalPages > 1 && (
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, width: '100%' }}>
					<Button
						variant="outlined"
						size="small"
						disabled={page === 0}
						onClick={() => setPage((prev) => Math.max(0, prev - 1))}
						sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569', minWidth: 90 }}
					>
						Anterior
					</Button>

					<Typography sx={{ flex: 1, textAlign: 'center', fontSize: 12, color: '#64748b' }}>
						Pagina {page + 1} de {totalPages}
					</Typography>

					<Button
						variant="outlined"
						size="small"
						disabled={page >= totalPages - 1}
						onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
						sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569', minWidth: 90, ml: 'auto' }}
					>
						Siguiente
					</Button>
				</Stack>
			)}

			<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
				<Box
					sx={{
						width: 18,
						height: 18,
						borderRadius: '9999px',
						border: '1px solid #94a3b8',
						color: '#64748b',
						fontSize: 12,
						fontWeight: 700,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					i
				</Box>
				<Typography sx={{ fontSize: 12, color: '#64748b' }}>
					Haz clic en el número de pregunta para una revisión
				</Typography>
			</Stack>

			</div>
		</div>
	);
}
